import type { ButtonGroupProps } from '@magicschool/spellbook/components/ButtonGroup';
import type { DropdownMenuSubProps } from '@magicschool/spellbook/components/DropdownMenu';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import type { SlotComponent, SlotProps } from '@magicschool/spellbook/components/Slot';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';

/** One item in the collapsed overflow menu, for a `ButtonGroup.Button` slot whose child is itself a `DropdownMenu` — its live menu content isn't introspected, so provide the items to show in the collapsed submenu explicitly. */
export type ButtonGroupCollapsedItem = {
  label: string;
  icon?: IconProps['name'];
  onSelect?: () => void;
  disabled?: boolean;
};

const buttonGroupButtonVariants = cva(['shrink-0', 'relative', 'min-w-0', '[&:has(:focus-visible)]:z-1', 'hover:z-1'], {
  variants: {
    orientation: {
      horizontal: [],
      vertical: [],
    },
    display: {
      connected: [
        '[&:not(:last-of-type)>*::before]:content-[""]',
        '[&:not(:last-of-type)>*::before]:absolute',
        '[&:not(:last-of-type)>*::before]:z-2',
        '[&:not(:last-of-type)>*::before]:bg-border-default',
        '[&:not(:last-of-type)>[data-border="true"]::before]:bg-transparent',
      ],
      inline: [],
    },
  },
  compoundVariants: [
    {
      display: 'connected',
      orientation: 'horizontal',
      className: [
        '[&:not(:first-of-type)>*]:rounded-l-none',
        '[&:not(:last-of-type)>*]:-me-px',
        '[&:not(:last-of-type)>*]:rounded-r-none',
        '[&:not(:last-of-type)>*::before]:w-px',
        '[&:not(:last-of-type)>*::before]:h-full',
        '[&:not(:last-of-type)>*::before]:top-0',
        '[&:not(:last-of-type)>*::before]:-right-[0.5px]',
        '[&:not(:last-of-type)>*::before]:scale-y-50',
      ],
    },
    {
      display: 'connected',
      orientation: 'vertical',
      className: [
        '[&:not(:first-of-type)>*]:rounded-t-none',
        '[&:not(:last-of-type)>*]:-mt-px',
        '[&:not(:last-of-type)>*]:rounded-b-none',
        '[&:not(:last-of-type)>*::before]:w-full',
        '[&:not(:last-of-type)>*::before]:h-px',
        '[&:not(:last-of-type)>*::before]:-bottom-[0.5px]',
        '[&:not(:last-of-type)>*::before]:left-0',
        '[&:not(:last-of-type)>*::before]:scale-x-50',
      ],
    },
  ],
});

type ButtonGroupButtonSlotProps = React.ComponentProps<'div'> & {
  orientation?: ButtonGroupProps['orientation'];
  display?: ButtonGroupProps['display'];
};

type CollapsibleButtonGroupButtonSlotProps = ButtonGroupButtonSlotProps & {
  /** Accessible label for this action when the group collapses. Falls back to a child `IconButton`'s own `label`; required otherwise (e.g. for a plain `Button` or a `DropdownMenu`-wrapped trigger). */
  collapsedLabel?: string;
  /** Icon for this action when the group collapses. Falls back to a child `IconButton`'s own `name`. */
  collapsedIcon?: IconProps['name'];
  /** Submenu items shown when this slot's child is a `DropdownMenu` and the group collapses. See `collapsedLabel`. */
  collapsedItems?: ButtonGroupCollapsedItem[];
  /** Any `DropdownMenu.Sub` prop for the collapsed submenu built from `collapsedItems` (`onOpenChange`, ...). */
  collapsedSubmenuProps?: Partial<Omit<DropdownMenuSubProps, 'children'>>;
  /** Position of this action in the collapsed menu, lowest first. Omit to keep its row (JSX) position — only needed when the collapsed order should differ from the row's. */
  collapsedOrder?: number;
};

// Also rendered as `CollapsibleButtonGroupButtonSlot` below — strip its collapse-only props too.
export const ButtonGroupButtonSlot = Slot<ButtonGroupButtonSlotProps>({
  displayName: 'ButtonGroup.Button',
  preventOverride: true,
  renderDefault: ({
    className,
    orientation = 'horizontal',
    display = 'connected',
    collapsedLabel: _collapsedLabel,
    collapsedIcon: _collapsedIcon,
    collapsedItems: _collapsedItems,
    collapsedSubmenuProps: _collapsedSubmenuProps,
    collapsedOrder: _collapsedOrder,
    ...props
  }: ButtonGroupButtonSlotProps & Partial<CollapsibleButtonGroupButtonSlotProps>) => (
    <div
      data-spellbook-name="button-group-button"
      className={cn(buttonGroupButtonVariants({ orientation, display }), className)}
      {...props}
    />
  ),
});

// Same component as `ButtonGroupButtonSlot`, widened only at the type level — keep it the same reference
// (not a second `Slot()`) so `child.type === Component` still matches when `CollapsibleButtonGroup` hands
// its children to `<ButtonGroup>` for the uncollapsed row.
export const CollapsibleButtonGroupButtonSlot: SlotComponent<SlotProps<CollapsibleButtonGroupButtonSlotProps>> = ButtonGroupButtonSlot;
